/* istanbul ignore file */
/* eslint-disable max-len */
import React from 'react';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ChannelGroupType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelGroupType';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import {
  CELSIUS_ID,
  FAHRENHEIT_ID, SPEED_FEET, SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import {
  adjustFromDisplay,
  findInternalTempChannelGroupFromChannelGroups,
  isBleSensor,
  isInternalMaxSensor,
  isWiredSensorAdapter,
  normalizeRaaChannelKey,
  parseChannelKey,
  trimRaaChannelKey,
} from '../../../../../utility/channelUtils';
import SensorsHeader from '../SensorsHeader';
import SingleReadingCard from '../sensors/cards/SingleReadingCard';
import BleSensorCard from '../sensors/cards/BleSensorCard/BleSensorCard';
import { getSecondsElapsed } from '../../../../../utility/displayValueUtils';
import AdapterSensorCard from '../sensors/cards/AdapterSensorCard/AdapterSensorCard';
import AdapterHeader from '../AdapterHeader';
import BasicSensorSettingsModal from '../../modals/settings/BasicSensorSettingsModal/BasicSensorSettingsModal';
import {
  fetchSingleDevice,
  getHiddenAdapterSensors,
  getHiddenRegularSensors,
  selectHiddenChannelGroups,
  toggleChannelGroupVisibility,
  updateSensor,
} from '../../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import { ShowHiddenSensorsButton } from '../../modals/ShowHiddenSensorsButton';
import { selectSessionInformation } from '../../../../../redux/slices/sessionSlice';
import formatTimestamp from '../../../../../utility/formatTimestamp';

function MaxDeviceSensors({
  sensors,
  deviceInfo,
  scalePreferences,
  isReadOnly = false,
}) {
  const dispatch = useDispatch();
  const hiddenChannelGroups = useSelector(selectHiddenChannelGroups);
  const { timezone, activeSubscriptionId } = useSelector(selectSessionInformation);
  const sessionInformation = useSelector(selectSessionInformation);
  const effectiveScalePreferences = scalePreferences || {
    temperatureScale: sessionInformation.temperatureScale,
    airSpeedScale: sessionInformation.airSpeedScale,
  };
  const [showSettingsModal, setShowSettingsModal] = React.useState(false);
  const internalSensor = sensors.find((sensor) => isInternalMaxSensor(sensor.channelKey));
  const internalChannelGroup = internalSensor && internalSensor.channelGroups
    .find((cg) => (isInternalMaxSensor(cg.channelKey) && parseChannelKey(cg.channelKey).channelGroupType === ChannelGroupType.TEMPERATURE.id));
  const internalChannelKey = internalChannelGroup && internalChannelGroup.channels
  && internalChannelGroup.channels[0]
    ? internalChannelGroup.channels[0].channelKey
    : undefined;
  const internalChannelParsed = internalChannelKey ? parseChannelKey(internalChannelKey) : null;
  const internalChannel = internalChannelParsed
    ? ChannelService.getChannelGroupChannel(
      internalChannelParsed.channelGroupType,
      internalChannelParsed.channelIndex,
      internalChannelParsed.channelType,
    )
    : null;
  const remainingSensors = sensors.filter((sensor) => !isInternalMaxSensor(sensor.channelKey));
  const bleSensors = remainingSensors.filter((sensor) => isBleSensor(sensor.channelGroups) && !isWiredSensorAdapter(sensor.channelGroups));
  const adapterSensors = remainingSensors.filter((sensor) => isWiredSensorAdapter(sensor.channelGroups));
  const assignmentStartDateTimestamp = deviceInfo && deviceInfo.deviceAssignmentStartDate
  && deviceInfo.deviceAssignmentStartDate.epochSecond
    ? deviceInfo.deviceAssignmentStartDate.epochSecond
    : 0;
  const internalGroupLastReceivedTimestamp = internalChannelGroup && internalChannelGroup.lastReceived;

  const hiddenAdapterSensors = React.useMemo(
    () => getHiddenAdapterSensors(hiddenChannelGroups, adapterSensors),
    [hiddenChannelGroups, adapterSensors],
  );

  const hiddenRegularSensors = React.useMemo(
    () => getHiddenRegularSensors(hiddenChannelGroups, adapterSensors),
    [hiddenChannelGroups, adapterSensors],
  );

  const isCurrentlyHidden = (channelKey) => hiddenChannelGroups.includes(channelKey);
  const isInternalSensorHidden = internalChannelGroup
    ? isCurrentlyHidden(internalChannelGroup.channelKey)
    : false;

  const toggleChannelGroupHidden = async (channelKey, label) => {
    if (isReadOnly) return;
    try {
      // passing through label because needed for api
      await dispatch(toggleChannelGroupVisibility({
        channelKey,
        label: label || internalChannelGroup.label,
        isHidden: !isCurrentlyHidden(channelKey),
      })).unwrap();
      await dispatch(fetchSingleDevice()).unwrap();
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  const handleSaveSettings = async (data) => {
    if (isReadOnly) return false;
    try {
      const updatedChannels = internalChannelGroup.channels.map((channel) => {
        const parsedKey = parseChannelKey(channel.channelKey);
        const channelInstance = ChannelService.getChannelGroupChannel(
          parsedKey.channelGroupType,
          parsedKey.channelIndex,
          parsedKey.channelType,
        );
        let adjust = 0;
        if (data.channelData[channel.channelKey]
            && data.channelData[channel.channelKey].adjust !== undefined) {
          adjust = parseInt(data.channelData[channel.channelKey].adjust, 10);
        }
        const normalizedAdjust = adjustFromDisplay(
          channelInstance,
          adjust,
          scalePreferences,
        );
        return {
          channelKey: trimRaaChannelKey(channel.channelKey),
          adjust: normalizedAdjust,
        };
      });
      const payload = {
        channelKey: internalChannelGroup.channelKey,
        channels: updatedChannels,
        label: data.label,
        isHidden: data.isHidden,
      };
      await dispatch(updateSensor(payload)).unwrap();
      await dispatch(fetchSingleDevice({ deviceId: deviceInfo.id })).unwrap();
      return true;
    } catch (error) {
      throw new Error(error.message || 'Failed to save sensor settings');
    }
  };

  return (
    <>
      <SensorsHeader
        handleSettingsClick={() => {}}
        startDate={assignmentStartDateTimestamp}
      />
      {!isReadOnly && (
      <ShowHiddenSensorsButton hiddenChannelGroups={hiddenRegularSensors} />
      )}
      {/* non adapter sensors */}
      <Row className="mx-auto card-container">
        {internalChannelGroup && internalChannel && !isInternalSensorHidden && (
          <Col key="hub" xs="auto" className="mb-2 px-1">
            <SingleReadingCard
              sensorName={internalChannelGroup.label}
              macAddress={internalChannelParsed.sensorMacAddress}
              typeName="MAX Base Station"
              lastUpdated={formatTimestamp(internalGroupLastReceivedTimestamp && internalGroupLastReceivedTimestamp.epochSecond, timezone, 'YYYY-MM-DD HH:mm:ss')}
              isHub
              isWireless={false}
              reading1={{
                title: 'Temperature',
                value: internalChannel.getDisplayValue(internalChannelGroup.channels[0].value, { scale: effectiveScalePreferences.temperatureScale, airSpeedScale: effectiveScalePreferences.airSpeedScale }),
                units: internalChannel.getUnits({ scale: effectiveScalePreferences.temperatureScale, airSpeedScale: effectiveScalePreferences.airSpeedScale }),
                // can check alert channels if we decide
                isTriggered: null,
                channelKey: normalizeRaaChannelKey(internalChannelKey, activeSubscriptionId),
              }}
              rssi={0}
              onSettingsClick={() => (isReadOnly ? () => {} : setShowSettingsModal(true))}
              isMuted={getSecondsElapsed(internalGroupLastReceivedTimestamp && internalGroupLastReceivedTimestamp.epochSecond) > 7200}
            />
          </Col>
        )}
        {/* if device is max assume all sensors besides adapters and internal are BLE sensors */}
        {
          // eslint-disable-next-line no-shadow
            bleSensors.length > 0 && bleSensors
              .filter((sensor) => {
                // use nonBle channelGroup.channelKey to toggle/check hidden status
                const channelGroup = sensor.channelGroups
                  .find((g) => parseChannelKey(g.channelKey).channelGroupType !== ChannelGroupType.BLUETOOTH_LE.id);
                return channelGroup && !isCurrentlyHidden(channelGroup.channelKey);
              })
              .map((sensor, index) => {
                const nonBleGroup = sensor.channelGroups
                  .find((g) => parseChannelKey(g.channelKey).channelGroupType !== ChannelGroupType.BLUETOOTH_LE.id);
                return (
                  <Col key={index} xs="auto" className="mb-2 px-1">
                    <BleSensorCard
                      sensor={sensor}
                      isHidden={(() => {
                        const channelGroup = sensor.channelGroups
                          .find((g) => parseChannelKey(g.channelKey).channelGroupType !== ChannelGroupType.BLUETOOTH_LE.id);
                        return channelGroup ? isCurrentlyHidden(channelGroup.channelKey) : false;
                      })()}
                      isMuted={getSecondsElapsed(nonBleGroup && nonBleGroup.lastReceived ? nonBleGroup.lastReceived.epochSecond : 0) > 7200}
                      isReadOnly={isReadOnly}
                    />
                  </Col>
                );
              })
        }
      </Row>
      {/* adapter sensors */}
      {adapterSensors.length > 0 && adapterSensors.map((sensor) => {
        const bleGroup = sensor.channelGroups.find(
          (group) => parseChannelKey(group.channelKey).channelGroupType === ChannelGroupType.BLUETOOTH_LE.id,
        );
        const isAdapterHidden = bleGroup && hiddenChannelGroups.includes(bleGroup.channelKey);
        const rssiChannel = bleGroup
          ? bleGroup.channels.find((channel) => parseChannelKey(channel.channelKey).channelType === ChannelType.RSSI.id)
          : null;
        const rssi = rssiChannel ? rssiChannel.value : 0;
        const adapterInternalTempChannelGroup = findInternalTempChannelGroupFromChannelGroups(sensor.channelGroups);
        const adapterNonInternalChannelGroups = sensor.channelGroups.filter((group) => group.channelKey !== adapterInternalTempChannelGroup.channelKey);
        const filteredHiddenAdapterSensors = hiddenAdapterSensors.filter((hiddenSensor) => parseChannelKey(hiddenSensor).sensorMacAddress === sensor.macAddress);
        if (isAdapterHidden) {
          return <ShowHiddenSensorsButton hiddenChannelGroups={[bleGroup.channelKey]} isAdapterHidden />;
        }
        return (
          <React.Fragment key={sensor.macAddress}>
            <div className="mb-2 px-1">
              <AdapterHeader
                label={bleGroup.label}
                adapterChannelGroups={sensor.channelGroups}
                lastReceived={bleGroup.lastReceived ? bleGroup.lastReceived.epochSecond : 0}
                macAddress={sensor.macAddress}
                rssi={rssi}
                isAdapterHidden={isCurrentlyHidden(sensor.channelKey)}
                onToggleAdapterHidden={isReadOnly ? () => {}
                  : () => toggleChannelGroupHidden(bleGroup.channelKey, bleGroup.label)}
                isReadOnly={isReadOnly}
              />
            </div>
            {
              !isReadOnly && (
              <ShowHiddenSensorsButton hiddenChannelGroups={filteredHiddenAdapterSensors} />
              )
            }
            <Row className="mx-auto card-container">
              {adapterInternalTempChannelGroup && !isCurrentlyHidden(adapterInternalTempChannelGroup.channelKey) && (
              <Col xs="auto" className="mb-2 px-1">
                <AdapterSensorCard
                  channelGroup={adapterInternalTempChannelGroup}
                  isHidden={isCurrentlyHidden(adapterInternalTempChannelGroup.channelKey)}
                  isMuted={getSecondsElapsed((adapterInternalTempChannelGroup.lastReceived && adapterInternalTempChannelGroup.lastReceived.epochSecond) || 0) > 7200}
                  isReadOnly={isReadOnly}
                />
              </Col>
              )}
              {adapterNonInternalChannelGroups
                .filter((group) => {
                  const parsedKey = parseChannelKey(group.channelKey);
                  return (
                    parsedKey.channelGroupType !== ChannelGroupType.BLUETOOTH_LE.id
                        && parsedKey.channelGroupType > 0
                  );
                })
                .filter((group) => !isCurrentlyHidden(group.channelKey))
                .sort((a, b) => {
                  const aMuted = getSecondsElapsed((a.lastReceived && a.lastReceived.epochSecond) || 0) > 7200;
                  const bMuted = getSecondsElapsed((b.lastReceived && b.lastReceived.epochSecond) || 0) > 7200;
                  // eslint-disable-next-line no-nested-ternary
                  return aMuted === bMuted ? 0 : aMuted ? 1 : -1;
                })
                .map((group, groupIndex) => (
                  <Col key={`${sensor.macAddress}-${groupIndex}`} xs="auto" className="mb-2 px-1">
                    <AdapterSensorCard
                      channelGroup={group}
                      isHidden={isCurrentlyHidden(group.channelKey)}
                      isMuted={getSecondsElapsed((group.lastReceived && group.lastReceived.epochSecond) || 0) > 7200}
                      isReadOnly={isReadOnly}
                    />
                  </Col>
                ))}
            </Row>

          </React.Fragment>
        );
      })}

      {showSettingsModal && internalSensor && internalChannelGroup && (
      <BasicSensorSettingsModal
        show={showSettingsModal}
        handleClose={() => setShowSettingsModal(false)}
        sensorLabel={internalChannelGroup.label}
        channelGroup={internalChannelGroup}
        onSave={handleSaveSettings}
        isHidden={isInternalSensorHidden}
        onToggleHidden={() => toggleChannelGroupHidden(internalChannelGroup.channelKey, internalChannelGroup.label)}
      />
      )}
    </>
  );
}

MaxDeviceSensors.defaultProps = {
  isReadOnly: false,
  scalePreferences: {
    temperatureScale: CELSIUS_ID,
    airSpeedScale: SPEED_METERS,
  },
};

MaxDeviceSensors.propTypes = {
  sensors: PropTypes.arrayOf(PropTypes.object).isRequired,
  deviceInfo: PropTypes.shape({
    id: PropTypes.number,
    uptime: PropTypes.string,
    pushStatus: PropTypes.number,
    isLinkConnected: PropTypes.bool,
    linkName: PropTypes.string,
    linkId: PropTypes.number,
    ethIp: PropTypes.string,
    wlanIp: PropTypes.string,
    isHttps: PropTypes.bool,
    httpPort: PropTypes.number,
    macAddress: PropTypes.string,
    serialnumber: PropTypes.string,
    firmware: PropTypes.string,
    type: PropTypes.string,
    warrantyEndDate: PropTypes.string,
    deviceAssignmentStartDate: PropTypes.shape({ epochSecond: PropTypes.number }),
  }).isRequired,
  isReadOnly: PropTypes.bool,
  scalePreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
  }),
};

export default MaxDeviceSensors;
